import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Menu, X, Lock, Building2, Users, ChevronDown } from 'lucide-react';
import { siteStage } from '../../config/siteStage';

// Navigation links — kept hardcoded because targets are technical wiring
// (route paths and section anchors), not editorial content.
const navigationLinks = [
  { label: "About", href: "/about", isPage: true },
  { label: "Residents", href: "/residents", isPage: true, children: [
    { label: "Residents overview", sub: "Your record, your control", href: "/residents", testid: "header-residents-link" },
    { label: "Resident early access pilot", sub: "Register and start your record", href: "/resident-pilot", testid: "header-pilot-link" },
    { label: "TRAs & community groups", sub: "ResiAX: Community for resident-led groups", href: "/resident-groups", testid: "header-groups-link" },
  ] },
  { label: "Providers", href: "/providers", isPage: true, children: [
    { label: "Provider pilot", sub: "Start small — landlords, agents & teams", href: "/providers", testid: "header-provider-pilot-link" },
    { label: "Enterprise & integration", sub: "Fit ResiAX around existing systems", href: "/providers/enterprise", testid: "header-enterprise-link" },
  ] },
  { label: "Resources", href: "/compliance", isPage: true, children: [
    { label: "Rights & compliance", sub: "Standards your home should meet", href: "/compliance", testid: "header-compliance-link" },
    { label: "Blog", sub: "News and practical guides", href: "/blog", testid: "header-blog-link" },
    { label: "FAQ", sub: "Plain answers to common questions", href: "/faq", testid: "header-faq-link" },
    { label: "Launch updates", sub: "Get product news by email", href: "/updates", testid: "header-updates-link" },
  ] },
  { label: "Contact", href: "/about#contact", isPage: true },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasBacklogAccess, setHasBacklogAccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setHasBacklogAccess(localStorage.getItem('resiax_access') === 'granted');
  }, [location]);

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.isPage) {
      navigate(link.href);
    } else if (link.href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(link.href);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      } else {
        const el = document.querySelector(link.href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isProviderPage = location.pathname === '/providers';
  const isDarkHero = isProviderPage && !isScrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <button onClick={handleLogoClick} className="flex items-center gap-2 group">
            <img 
              src="/logo.png" 
              alt="ResiAX" 
              className="h-10 w-10 object-contain"
            />
            <div className="flex items-baseline">
              <span className="font-bold text-xl text-[#B5541B]">RESI</span>
              <span className={`font-bold text-xl transition-colors duration-300 ${isDarkHero ? 'text-white' : 'text-[#1F1E1E]'}`}>AX</span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-7">
            {navigationLinks.map((link) => {
              const isActive = link.isPage 
                ? location.pathname === link.href 
                : location.pathname === '/' && location.hash === link.href;
              
              const isFeatures = link.href === '/features';
              const showLock = isFeatures && !hasBacklogAccess;

              if (link.children) {
                const providersActive = link.children.some((c) => location.pathname.startsWith(c.href));
                return (
                  <div key={link.label} className="relative group">
                    <button
                      onClick={() => handleNavClick(link)}
                      data-testid={`header-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-nav`}
                      className={`text-sm font-medium transition-colors duration-200 relative flex items-center gap-1 ${
                        providersActive
                          ? 'text-[#B5541B]'
                          : isDarkHero
                            ? 'text-white/70 hover:text-[#D4722F]'
                            : 'text-[#3B3535] hover:text-[#B5541B]'
                      }`}
                    >
                      {link.label}
                      <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-200" />
                      <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#B5541B] transition-all duration-300 ${
                        providersActive ? 'w-full' : 'w-0 group-hover:w-full'
                      }`} />
                    </button>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-64">
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            data-testid={child.testid}
                            className="block px-4 py-2.5 text-sm font-medium text-[#3B3535] hover:bg-[#B5541B]/10 hover:text-[#B5541B] transition-colors"
                          >
                            {child.label}
                            <span className="block text-xs font-normal text-[#3B3535]/60">{child.sub}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link)}
                  className={`text-sm font-medium transition-colors duration-200 relative group flex items-center gap-1 ${
                    isActive 
                      ? 'text-[#B5541B]' 
                      : isDarkHero 
                        ? 'text-white/70 hover:text-[#D4722F]' 
                        : 'text-[#3B3535] hover:text-[#B5541B]'
                  }`}
                >
                  {link.label}
                  {showLock && <Lock className="w-3 h-3 opacity-50" />}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#B5541B] transition-all duration-300 ${
                    isActive ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </button>
              );
            })}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            {!isProviderPage && (
              <Button
                asChild
                variant="outline"
                className="border border-[#B5541B]/30 text-[#B5541B] hover:bg-[#B5541B]/10 font-medium px-4 py-2 rounded-full text-sm transition-all duration-300"
                data-testid="header-tras-btn"
              >
                <Link to="/resident-groups">
                  <Users className="mr-1.5 w-3.5 h-3.5" />
                  TRA
                </Link>
              </Button>
            )}
            <Button
              asChild
              variant="outline"
              className="border border-[#B5541B]/40 text-[#B5541B] hover:bg-[#B5541B]/10 font-medium px-4 py-2 rounded-full text-sm transition-all duration-300"
              data-testid="header-login-btn"
            >
              <a href="https://app.resiax.com" target="_blank" rel="noopener noreferrer">
                Log In
              </a>
            </Button>
            <Button
              asChild
              className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all duration-300 text-sm"
              data-testid="header-register-btn"
            >
              <Link to="/resident-pilot#register">
                Join early access
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#1F1E1E]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1F1E1E]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-2 pb-4 border-t border-gray-100 bg-white/95 backdrop-blur-md rounded-b-2xl">
            <nav className="flex flex-col gap-1 pt-4">
              {navigationLinks.map((link) => (
                <React.Fragment key={link.label}>
                  {link.children ? (
                    <>
                      <span className="px-4 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-[#3B3535]/50">{link.label}</span>
                      {link.children.map((child) => (
                        <button
                          key={child.href}
                          onClick={() => handleNavClick({ ...child, isPage: true })}
                          className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-[#3B3535] hover:bg-[#B5541B]/10 hover:text-[#B5541B] transition-colors text-left font-medium"
                        >
                          {child.label}
                        </button>
                      ))}
                    </>
                  ) : (
                    <button
                      onClick={() => handleNavClick(link)}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-[#3B3535] hover:bg-[#B5541B]/10 hover:text-[#B5541B] transition-colors text-left font-medium"
                    >
                      {link.label}
                    </button>
                  )}
                </React.Fragment>
              ))}
              <div className="px-4 pt-2 flex flex-col gap-2">
                <Button
                  asChild
                  className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold rounded-full shadow-md w-full"
                  data-testid="mobile-register-btn"
                >
                  <Link to="/resident-pilot#register">
                    Register for early access
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-[#B5541B] text-[#B5541B] font-semibold rounded-full w-full"
                  data-testid="mobile-login-btn"
                >
                  <a href="https://app.resiax.com" target="_blank" rel="noopener noreferrer">
                    Log In
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border border-[#B5541B]/40 text-[#B5541B] font-medium rounded-full w-full"
                  data-testid="mobile-providers-btn"
                >
                  <Link to="/providers">
                    <Building2 className="mr-1.5 w-4 h-4" />
                    For Housing Providers
                  </Link>
                </Button>
                <Link
                  to="/providers/enterprise"
                  data-testid="mobile-enterprise-link"
                  className="text-center text-sm font-medium text-[#3B3535]/70 hover:text-[#B5541B] transition-colors py-1"
                >
                  Enterprise &amp; integration →
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
